import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { extractHttpErrorMessage } from '../../../../shared/utils/http-error.util';
import { ToastService } from '../../../../shared/services/toast.service';
import { SkillsApiService } from '../../../../features/skills/skills-api.service';
import type { AgentSkill } from '../../../../features/skills/skills.models';
import { CreateSkill } from '../create-skill/create-skill';

@Component({
  selector: 'app-skills-gallery',
  imports: [CommonModule, DialogModule, ButtonModule, ConfirmDialogModule, TooltipModule, CreateSkill],
  providers: [ConfirmationService],
  templateUrl: './skills-gallery.html',
  styleUrl: './skills-gallery.css',
})
export class SkillsGallery {
  private readonly api = inject(SkillsApiService);
  private readonly toast = inject(ToastService);
  private readonly confirmation = inject(ConfirmationService);

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  readonly loading = signal(false);
  readonly errorText = signal<string | null>(null);
  readonly items = signal<AgentSkill[]>([]);
  readonly deletingId = signal<string | null>(null);

  editVisible = false;
  editingSkill: AgentSkill | null = null;
  readonly deleteConfirmKey = 'skills-gallery-delete';

  onDialogShow(): void {
    this.editVisible = false;
    this.editingSkill = null;
    this.load();
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  load(): void {
    this.loading.set(true);
    this.errorText.set(null);
    this.api.listSkills().subscribe({
      next: (res) => {
        this.loading.set(false);
        this.items.set(res.items ?? []);
      },
      error: (err: unknown) => {
        this.loading.set(false);
        this.items.set([]);
        this.errorText.set(extractHttpErrorMessage(err, 'Could not load skills.'));
      },
    });
  }

  promptPreview(skill: AgentSkill): string {
    const text = String(skill.prompt ?? '').trim();
    const max = 180;
    if (text.length <= max) {
      return text;
    }
    return `${text.slice(0, max).trim()}…`;
  }

  parameterLabel(skill: AgentSkill): string {
    const names = skill.parameters ?? [];
    if (names.length === 0) {
      return '';
    }
    return names.join(', ');
  }

  startCreate(): void {
    this.editingSkill = null;
    this.editVisible = true;
  }

  startEdit(skill: AgentSkill): void {
    this.editingSkill = skill;
    this.editVisible = true;
  }

  onSkillSaved(skill: AgentSkill): void {
    const previousId = this.editingSkill?.id;
    if (!previousId) {
      this.items.update((rows) => [skill, ...rows.filter((row) => row.id !== skill.id)]);
    } else {
      this.items.update((rows) =>
        rows.map((row) => (row.id === previousId || row.id === skill.id ? skill : row)),
      );
    }
    this.editingSkill = null;
  }

  confirmDelete(skill: AgentSkill): void {
    this.confirmation.confirm({
      key: this.deleteConfirmKey,
      header: 'Delete skill',
      message: `Delete /${skill.slash}? It will leave the slash menu. You can create that name again later.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      rejectButtonStyleClass: 'p-button-secondary',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteSkill(skill),
    });
  }

  private deleteSkill(skill: AgentSkill): void {
    this.deletingId.set(skill.id);
    this.api.deleteSkill(skill.id).subscribe({
      next: () => {
        this.deletingId.set(null);
        this.items.update((rows) => rows.filter((row) => row.id !== skill.id));
        this.toast.success(`Deleted /${skill.slash}`);
      },
      error: (err: unknown) => {
        this.deletingId.set(null);
        this.toast.error(extractHttpErrorMessage(err, 'Could not delete this skill.'));
      },
    });
  }
}
